import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { cn } from '@/lib/utils'
import { formsApi } from '@/api/formsApi'
import { versionLabel } from '@/lib/versioning'
import type { LisPdfMapping } from '@/api/lisResultsApi'
import { MappingProviderFields } from '@/components/integrations/MappingProviderFields'
import { orderingProviderLine } from '@/lib/lis/orderingProvider'
import {
  emptyPdfDraft,
  pdfDraftBody,
  pdfDraftErrors,
  pdfDraftFrom,
  pdfFieldOptions,
  type PdfMappingDraft,
} from '@/lib/lis/pdfMapping'
import {
  useCreateLisPdfMapping,
  useLisPdfMappings,
  useRemoveLisPdfMapping,
  useUpdateLisPdfMapping,
} from '@/hooks/useLisResults'

const FORMS_KEY = ['form-definitions', 'for-lis-mapping'] as const

/**
 * PDF outgoing mappings (z8nrz7fgcp AC1): which already-generated PDF this
 * integration sends to Epic. It goes UNSOLICITED (MedStar): nothing is ordered
 * first, the PDF is filed under the order code set here (e.g. CTPROCRPT) as one
 * ED result line, once the form is completed and someone presses Send PDF to
 * Epic on the product. The PDF is sent unchanged; nothing is generated.
 */
export function LisPdfMappingSection({
  integrationId,
  resultTests,
}: {
  integrationId: string
  /** Tests that already send results on this integration: a PDF goes under its own code. */
  resultTests: string[]
}) {
  const [editing, setEditing] = useState<'new' | string | null>(null)
  const mappings = useLisPdfMappings(integrationId)
  const remove = useRemoveLisPdfMapping()
  const rows = mappings.data ?? []
  const { data: defsResult } = useQuery({ queryKey: FORMS_KEY, queryFn: () => formsApi.listDefinitions() })
  const formName = (id: string) => defsResult?.items.find((d) => d.id === id)?.name ?? 'Form'

  return (
    <div className='space-y-4'>
      <p className='text-xs text-muted-foreground'>
        When the <strong>form</strong> is completed, its <strong>attachment</strong> can be sent to Epic as a PDF, unchanged,
        from the product page (Send PDF to Epic). It goes on its own: no order is placed first. Title21 makes the order
        number, under the order code below. Epic users see it in Chart Review with a link that opens the PDF.
      </p>

      <div className='rounded-xl border bg-white shadow-sm'>
        <div className='border-b px-4 py-3'>
          <h3 className='text-sm font-medium'>PDF mappings</h3>
          <p className='text-xs text-muted-foreground'>
            One per order code. The file is the one attached to the field when the PDF is sent.
          </p>
        </div>
        <div className='overflow-x-persistent'>
          <table className='w-full text-sm'>
            <thead className='bg-muted/40 text-left text-xs text-muted-foreground'>
              <tr>
                <th className='px-3 py-2'>Form → Attachment</th>
                <th className='px-3 py-2'>Order code</th>
                <th className='px-3 py-2'>Result line</th>
                <th className='px-3 py-2'>Ordering provider</th>
                <th className='px-3 py-2 text-right'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.isPending ? (
                <tr>
                  <td colSpan={5} className='px-3 py-6 text-center text-muted-foreground'>
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className='px-3 py-6 text-center text-muted-foreground'>
                    No PDF mappings yet.
                  </td>
                </tr>
              ) : null}
              {rows.map((m) => (
                <tr key={m.id} className={cn('border-t', editing === m.id && 'bg-primary/5')}>
                  <td className='px-3 py-2'>
                    {formName(m.formDefinitionId)} → <FieldLabel formId={m.formDefinitionId} fieldId={m.fieldId} />
                  </td>
                  <td className='px-3 py-2'>
                    <span className='font-mono'>{m.testCode}</span>
                    {m.testName ? <span className='text-muted-foreground'> — {m.testName}</span> : null}
                  </td>
                  <td className='px-3 py-2'>
                    <span className='font-mono'>{m.code}</span>
                    {m.text ? <span className='text-muted-foreground'> — {m.text}</span> : null}
                  </td>
                  <td className='px-3 py-2'>{orderingProviderLine(m.orderingProvider) ?? 'From LIS Stages'}</td>
                  <td className='px-3 py-2'>
                    <div className='flex justify-end gap-1'>
                      <Button
                        type='button'
                        size='icon'
                        variant='ghost'
                        className='h-7 w-7'
                        title={`Edit PDF mapping ${m.testCode}`}
                        aria-label={`Edit PDF mapping ${m.testCode}`}
                        onClick={() => setEditing(m.id)}
                      >
                        <Pencil className='h-3.5 w-3.5' />
                      </Button>
                      <Button
                        type='button'
                        size='icon'
                        variant='ghost'
                        className='h-7 w-7'
                        title={`Delete PDF mapping ${m.testCode}`}
                        aria-label={`Delete PDF mapping ${m.testCode}`}
                        disabled={remove.isPending}
                        onClick={() => {
                          if (!window.confirm(`Delete the PDF mapping for ${m.testCode}?`)) return
                          remove.mutate(m.id, { onSuccess: () => editing === m.id && setEditing(null) })
                        }}
                      >
                        <Trash2 className='h-3.5 w-3.5' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {remove.error ? <p className='px-4 pt-3 text-sm text-destructive'>{remove.error.message}</p> : null}

        <div className='p-4'>
          {editing ? (
            <PdfMappingForm
              key={editing}
              integrationId={integrationId}
              mapping={editing === 'new' ? null : (rows.find((m) => m.id === editing) ?? null)}
              pdfTests={rows.filter((m) => m.id !== editing).map((m) => m.testCode)}
              resultTests={resultTests}
              onDone={() => setEditing(null)}
            />
          ) : (
            <Button size='sm' variant='outline' onClick={() => setEditing('new')}>
              <Plus className='mr-1 h-4 w-4' />
              Add PDF mapping
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function FieldLabel({ formId, fieldId }: { formId: string; fieldId: string }) {
  const { data: form } = useQuery({
    queryKey: ['form-definition', formId],
    queryFn: () => formsApi.getDefinition(formId),
  })
  const label = pdfFieldOptions(form).find((f) => f.id === fieldId)?.label
  return <span>{label ?? (form ? 'a field no longer on the form' : '…')}</span>
}

/** Add or edit one PDF mapping. Errors show beside the field once Save is pressed. */
function PdfMappingForm({
  integrationId,
  mapping,
  pdfTests,
  resultTests,
  onDone,
}: {
  integrationId: string
  mapping: LisPdfMapping | null
  pdfTests: string[]
  resultTests: string[]
  onDone: () => void
}) {
  const [draft, setDraft] = useState<PdfMappingDraft>(() => (mapping ? pdfDraftFrom(mapping) : emptyPdfDraft()))
  const [submitted, setSubmitted] = useState(false)
  const create = useCreateLisPdfMapping()
  const update = useUpdateLisPdfMapping()
  const pending = create.isPending || update.isPending
  const serverError = create.error ?? update.error

  const { data: defsResult } = useQuery({ queryKey: FORMS_KEY, queryFn: () => formsApi.listDefinitions() })
  const definitions = defsResult?.items ?? []
  const { data: form } = useQuery({
    queryKey: ['form-definition', draft.formDefinitionId],
    enabled: !!draft.formDefinitionId,
    queryFn: () => formsApi.getDefinition(draft.formDefinitionId),
  })
  const fieldOptions = pdfFieldOptions(form).map((f) => ({ value: f.id, label: f.label }))

  const errors = submitted ? pdfDraftErrors(draft, pdfTests, resultTests) : {}
  const set = <K extends keyof PdfMappingDraft>(key: K, value: PdfMappingDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const submit = () => {
    setSubmitted(true)
    if (Object.keys(pdfDraftErrors(draft, pdfTests, resultTests)).length > 0) return
    const body = pdfDraftBody(draft)
    if (mapping) update.mutate({ id: mapping.id, body }, { onSuccess: onDone })
    else create.mutate({ ...body, integrationId }, { onSuccess: onDone })
  }

  return (
    <div className='space-y-3 rounded-lg border bg-muted/20 p-3'>
      <p className='text-sm font-medium'>{mapping ? `Edit PDF mapping ${mapping.testCode}` : 'Add PDF mapping'}</p>

      <div className='grid gap-3 sm:grid-cols-2'>
        <div className='grid gap-1.5'>
          <Label>Form the PDF is attached to</Label>
          <SearchableSelect
            value={draft.formDefinitionId}
            // A field of another form would not exist there, so changing the form clears it.
            onChange={(v) => setDraft((d) => ({ ...d, formDefinitionId: v, fieldId: '' }))}
            options={definitions.map((d) => ({
              value: d.id,
              label: `${d.name} (${d.versionName ?? versionLabel(d.version)})`,
            }))}
            selectedOption={
              draft.formDefinitionId && form
                ? { value: draft.formDefinitionId, label: `${form.name} (${form.versionName ?? versionLabel(form.version)})` }
                : undefined
            }
            placeholder='Select a form'
          />
          {errors.formDefinitionId ? <p className='text-xs text-destructive'>{errors.formDefinitionId}</p> : null}
        </div>
        <div className='grid gap-1.5'>
          <Label>Attachment field</Label>
          <SearchableSelect
            value={draft.fieldId}
            onChange={(v) => set('fieldId', v)}
            options={fieldOptions}
            selectedOption={fieldOptions.find((o) => o.value === draft.fieldId)}
            placeholder={draft.formDefinitionId ? 'Select a field' : 'Select a form first'}
          />
          {errors.fieldId ? <p className='text-xs text-destructive'>{errors.fieldId}</p> : null}
          {form && fieldOptions.length === 0 ? (
            <p className='text-xs text-muted-foreground'>
              This form has no field that takes a PDF. Use an Attachment field, or turn on attachments for a field.
            </p>
          ) : null}
        </div>
      </div>

      <div className='grid gap-3 sm:grid-cols-[180px_1fr]'>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-pdf-order-code'>Order code (OBR-4)</Label>
          <Input
            id='lis-pdf-order-code'
            value={draft.testCode}
            onChange={(e) => set('testCode', e.target.value)}
            className='font-mono'
          />
          {errors.testCode ? <p className='text-xs text-destructive'>{errors.testCode}</p> : null}
        </div>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-pdf-order-name'>
            Order name <span className='font-normal text-muted-foreground'>(optional)</span>
          </Label>
          <Input id='lis-pdf-order-name' value={draft.testName} onChange={(e) => set('testName', e.target.value)} />
          {errors.testName ? <p className='text-xs text-destructive'>{errors.testName}</p> : null}
        </div>
      </div>
      <p className='-mt-1 text-[11px] text-muted-foreground'>
        The order the PDF is filed under in Epic. Nothing is ordered first: Title21 makes the order number when the PDF
        is sent. The code must be built in Epic.
      </p>

      <div className='grid gap-3 sm:grid-cols-[180px_1fr]'>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-pdf-code'>Result line code (OBX-3.1)</Label>
          <Input
            id='lis-pdf-code'
            value={draft.code}
            onChange={(e) => set('code', e.target.value)}
            className='font-mono'
          />
          {errors.code ? <p className='text-xs text-destructive'>{errors.code}</p> : null}
        </div>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-pdf-text'>Result line text (OBX-3.2)</Label>
          <Input id='lis-pdf-text' value={draft.text} onChange={(e) => set('text', e.target.value)} />
          {errors.text ? <p className='text-xs text-destructive'>{errors.text}</p> : null}
        </div>
      </div>

      <MappingProviderFields idPrefix='lis-pdf-provider' value={draft.provider} onChange={(v) => set('provider', v)} />

      <p className='text-xs text-muted-foreground'>
        Sent as <span className='font-mono'>OBR|1|…||{draft.testCode.trim() || 'CODE'}^{draft.testName.trim()}^L</span> and{' '}
        <span className='font-mono'>OBX|1|ED|{draft.code.trim() || 'CODE'}^{draft.text.trim() || 'TEXT'}^L||^Application^PDF^Base64^…</span>.
      </p>

      {serverError ? <p className='text-sm text-destructive'>{serverError.message}</p> : null}

      <div className='flex justify-end gap-2'>
        <Button type='button' size='sm' variant='outline' onClick={onDone} disabled={pending}>
          Cancel
        </Button>
        <Button type='button' size='sm' onClick={submit} disabled={pending}>
          {pending ? <Loader2 className='mr-1 h-4 w-4 animate-spin' /> : null}
          {mapping ? 'Save' : 'Add'}
        </Button>
      </div>
    </div>
  )
}
